import React, { useEffect, useState } from "react";
import {
  Container,
  Title,
  TextInput,
  Group,
  Text,
  Stack,
  Loader,
  Button,
  Card,
  Avatar,
  Grid,
  Pagination,
} from "@mantine/core";
import {
  IconSearch,
  IconBuildingWarehouse,
  IconPhone,
  IconMail,
  IconMapPin,
} from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { fetchWarehouses } from "../store/slices/warehousesSlice";
import { WarehouseResponse } from "types/responses.type";

const Warehouses = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    items: warehouses,
    loading,
    error,
  } = useSelector((state: RootState) => state.warehouses);
  const [search, setSearch] = useState("");
  const [filteredWarehouses, setFilteredWarehouses] = useState<
    WarehouseResponse[]
  >([]);
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = 10;

  const paginatedWarehouses = filteredWarehouses.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  useEffect(() => {
    dispatch(fetchWarehouses());
  }, [dispatch]);

  useEffect(() => {
    setFilteredWarehouses([
      ...warehouses.filter((warehouse) => {
        const searchString = `${warehouse.name ?? ""} ${
          warehouse.descriptions ?? ""
        } ${warehouse.place?.address ?? ""} ${warehouse.phoneNumber ?? ""} ${
          warehouse.email ?? ""
        } ${warehouse.pointOfContact ?? ""}`.toLowerCase();
        return searchString.includes(search.toLowerCase());
      }),
    ]);
    setActivePage(1); // Reset to first page on search or warehouse list change
  }, [search, warehouses]);

  return (
    <Container size="xl" py="xl">
      <Stack>
        <Group justify="space-between" align="center">
          <Title order={2}>Warehouses</Title>
        </Group>
        <Group justify="space-between" align="center">
          {warehouses.length > 0 && (
            <TextInput
              placeholder="Search warehouses..."
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              w={300}
            />
          )}
          <Pagination
            total={Math.ceil(filteredWarehouses.length / itemsPerPage)}
            p={activePage}
            onChange={setActivePage}
          />
        </Group>

        {loading && <Loader />}
        {error && <Text c="red">{error}</Text>}
        {!loading && warehouses.length === 0 && (
          <Stack align="center" justify="center" style={{ height: "50vh" }}>
            <IconBuildingWarehouse size={48} color="gray" />
            <Text ta="center" c="dimmed">
              No warehouses found.
            </Text>
          </Stack>
        )}
        {!loading && paginatedWarehouses.length > 0 && (
          <Grid>
            {paginatedWarehouses.map((warehouse) => (
              <Grid.Col
                key={`warehouse-${warehouse.warehouseId}`}
                span={{ base: 12, lg: 6 }}
              >
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Group>
                    <Avatar color="blue" radius="xl">
                      <IconBuildingWarehouse size={24} />
                    </Avatar>
                    <Stack gap={0}>
                      <Text fw={500}>
                        {(warehouse.name ?? "") +
                          (warehouse.descriptions
                            ? ` - ${warehouse.descriptions}`
                            : "")}
                      </Text>
                      <Group gap="xs">
                        <IconMapPin size={16} color="gray" />
                        <Text>{warehouse.place?.address}</Text>
                      </Group>
                      {warehouse.phoneNumber && (
                        <Group gap="xs">
                          <IconPhone size={16} color="gray" />
                          <Text size="sm" c="dimmed">
                            {warehouse.phoneNumber}
                            {warehouse.pointOfContact
                              ? ` (${warehouse.pointOfContact})`
                              : ""}
                          </Text>
                        </Group>
                      )}
                      {warehouse.email && (
                        <Group gap="xs">
                          <IconMail size={16} color="gray" />
                          <Text size="sm" c="dimmed">
                            {warehouse.email}
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>
    </Container>
  );
};

export default Warehouses;
